import React, { useEffect, useState } from 'react';
import { newNGApi } from '../services/newNGApi';
import { useNewNG } from '../context/NewNGContext';
import styles from '../NewNG.module.css';

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  image: string;
  jobId: string;
  isLive?: boolean;
  airDate?: string;
  createdAt?: string;
}

interface NewsGridProps {
  onVideoFocus?: () => void;
}

export const NewsGrid: React.FC<NewsGridProps> = ({ onVideoFocus }) => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { dispatch } = useNewNG();

  // Handle article click to change current job
  const handleArticleClick = async (article: NewsArticle) => {
    try {


      // Find the job in the current jobs list or fetch fresh data
      const jobs = await newNGApi.getJobs();
      const targetJob = jobs.find(job => job.job_id === article.jobId);

      if (targetJob) {
        dispatch({ type: 'SET_CURRENT_JOB', payload: targetJob });

        // Focus the video section after job change
        if (onVideoFocus) {
          onVideoFocus();
        }
      } else {
        console.warn('Job not found:', article.jobId);
      }
    } catch (error) {
      console.error('Failed to switch job:', error);
    }
  };

  // Load real news data from backend
  useEffect(() => {
    const loadNewsData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 1. Fetch all available jobs
        const jobs = await newNGApi.getJobs();

        // Filter for completed jobs only - check both status fields
        const completedJobs = jobs.filter(job =>
          job.status === 'completed' || job.overall_status === 'completed'
        );

        // If no completed jobs, use all jobs for now (for debugging)
        const jobsToProcess = completedJobs.length > 0 ? completedJobs : jobs;

        // 2. Get headlines for each job and create articles
        const newsArticles: NewsArticle[] = [];

        for (const job of jobsToProcess.slice(0, 12)) { // Limit to 12 articles
          try {
            // Get headlines for this job
            const headlines = await newNGApi.getJobHeadlines(job.job_id);

            // Get job metadata for broadcast date
            let jobMetadata = null;
            try {
              jobMetadata = await newNGApi.getJobInfo(job.job_id);
            } catch (metaError) {
              console.warn(`No job metadata for ${job.job_id}`);
            }

            if (headlines.length > 0) {
              // Use the first headline as the main article
              const firstHeadline = headlines[0];

              // Create b-roll image URL for the first scene
              const brollImageUrl = `/news/out/${job.job_id}/render_stage/scene_0/broll.png`;

              // Determine broadcast date (prefer job metadata, fallback to job created_at)
              const broadcastDate = jobMetadata?.created_at || job.created_at;

              const article: NewsArticle = {
                id: job.job_id,
                title: firstHeadline.title,
                summary: firstHeadline.summary || `Latest news on ${job.title || 'current events'}`,
                image: brollImageUrl,
                jobId: job.job_id,
                isLive: firstHeadline.is_live,
                airDate: broadcastDate,
                createdAt: job.created_at
              };

              newsArticles.push(article);
            }
          } catch (headlineError) {
            console.warn(`Failed to get headlines for job ${job.job_id}:`, headlineError);
            // Create a fallback article with job info
            const fallbackArticle: NewsArticle = {
              id: job.job_id,
              title: job.title || `News Update ${job.job_id.slice(0, 8)}`,
              summary: job.description || 'Latest news and updates',
              image: `/news/out/${job.job_id}/render_stage/scene_0/broll.png`,
              jobId: job.job_id,
              isLive: false,
              airDate: job.created_at,
              createdAt: job.created_at
            };
            newsArticles.push(fallbackArticle);
          }
        }

        // Sort articles by air date (newest first) and mark latest as LIVE
        newsArticles.sort((a, b) => {
          const dateA = new Date(a.airDate || '').getTime();
          const dateB = new Date(b.airDate || '').getTime();
          return dateB - dateA; // Descending order (newest first)
        });

        // Mark only articles from the latest day as LIVE
        if (newsArticles.length > 0) {
          const latestDate = newsArticles[0].airDate;
          const latestDateObj = new Date(latestDate || '');

          // Get the start and end of the latest date (same calendar day)
          const latestDateStart = new Date(latestDateObj.getFullYear(), latestDateObj.getMonth(), latestDateObj.getDate());
          const latestDateEnd = new Date(latestDateStart.getTime() + (24 * 60 * 60 * 1000) - 1); // End of same day



          newsArticles.forEach(article => {
            const articleTime = new Date(article.airDate || '').getTime();
            const wasLiveBefore = article.isLive;
            // Mark as LIVE only if from the same calendar day as the latest article
            if (articleTime >= latestDateStart.getTime() && articleTime <= latestDateEnd.getTime()) {
              article.isLive = true;
            } else {
              article.isLive = false;
            }

            if (wasLiveBefore !== article.isLive) {

            }
          });
        }


        setArticles(newsArticles);

      } catch (err) {
        console.error('Error loading news data:', err);
        setError('Failed to load news data');

        // Fallback to sample data if backend fails
        const now = new Date();
        const sampleDates = [
          new Date(now.getTime() - 1 * 60 * 60 * 1000).toISOString(), // 1 hour ago
          new Date(now.getTime() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
          new Date(now.getTime() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago
          new Date(now.getTime() - 12 * 60 * 60 * 1000).toISOString(), // 12 hours ago
          new Date(now.getTime() - 18 * 60 * 60 * 1000).toISOString(), // 18 hours ago
          new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
          new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
          new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
          new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000).toISOString(), // 4 days ago
          new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
          new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString(), // 6 days ago
          new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week ago
        ];

        setArticles([
          { id: '1', title: "Market analysis: Tech stocks reach new heights", summary: "Technology companies continue to dominate market performance with innovative solutions driving growth.", image: "📈", jobId: 'fallback-1', airDate: sampleDates[0], isLive: true },
          { id: '2', title: "Climate action: Global initiatives gain momentum", summary: "International cooperation leads to significant progress in environmental protection efforts.", image: "🌍", jobId: 'fallback-2', airDate: sampleDates[1], isLive: true },
          { id: '3', title: "Healthcare innovation: Breakthrough treatments emerge", summary: "Medical research advances promise improved outcomes for patients worldwide.", image: "🏥", jobId: 'fallback-3', airDate: sampleDates[2], isLive: true },
          { id: '4', title: "Education reform: New learning approaches adopted", summary: "Educational institutions embrace technology to enhance student learning experiences.", image: "🎓", jobId: 'fallback-4', airDate: sampleDates[3], isLive: true },
          { id: '5', title: "Space exploration: Mars mission achieves new milestones", summary: "Latest developments in space exploration show promising results for future missions.", image: "🚀", jobId: 'fallback-5', airDate: sampleDates[4], isLive: true },
          { id: '6', title: "Artificial intelligence: Revolutionary applications discovered", summary: "AI technology breakthroughs open new possibilities across various industries.", image: "🤖", jobId: 'fallback-6', airDate: sampleDates[5], isLive: false },
          { id: '7', title: "Renewable energy: Solar efficiency records broken", summary: "Solar panel technology reaches unprecedented efficiency levels.", image: "☀️", jobId: 'fallback-7', airDate: sampleDates[6], isLive: false },
          { id: '8', title: "Digital transformation: Companies accelerate adoption", summary: "Businesses worldwide rapidly embrace digital technologies for competitive advantage.", image: "💻", jobId: 'fallback-8', airDate: sampleDates[7], isLive: false },
          { id: '9', title: "Cybersecurity: New protection protocols implemented", summary: "Enhanced security measures protect against evolving digital threats.", image: "🔒", jobId: 'fallback-9', airDate: sampleDates[8], isLive: false },
          { id: '10', title: "Transportation: Electric vehicle market expands rapidly", summary: "Electric vehicle adoption accelerates with new models and infrastructure.", image: "⚡", jobId: 'fallback-10', airDate: sampleDates[9], isLive: false },
          { id: '11', title: "Entertainment: Streaming platforms revolutionize media", summary: "Digital streaming continues to transform how we consume entertainment content.", image: "🎬", jobId: 'fallback-11', airDate: sampleDates[10], isLive: false },
          { id: '12', title: "Finance: Cryptocurrency regulations evolve globally", summary: "New regulatory frameworks shape the future of digital currency markets.", image: "💰", jobId: 'fallback-12', airDate: sampleDates[11], isLive: false }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    loadNewsData();
  }, []);

  // Handle image load error - fallback to emoji
  const handleImageError = (articleId: string) => {
    setArticles(prev => prev.map(article =>
      article.id === articleId
        ? { ...article, image: getFallbackEmoji(article.title) }
        : article
    ));
  };

  // Get fallback emoji based on title keywords
  const getFallbackEmoji = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('tech') || lowerTitle.includes('market')) return '📈';
    if (lowerTitle.includes('climate') || lowerTitle.includes('environment')) return '🌍';
    if (lowerTitle.includes('health') || lowerTitle.includes('medical')) return '🏥';
    if (lowerTitle.includes('education') || lowerTitle.includes('learning')) return '🎓';
    if (lowerTitle.includes('space') || lowerTitle.includes('mars')) return '🚀';
    if (lowerTitle.includes('ai') || lowerTitle.includes('artificial')) return '🤖';
    if (lowerTitle.includes('energy') || lowerTitle.includes('solar')) return '☀️';
    if (lowerTitle.includes('digital') || lowerTitle.includes('technology')) return '💻';
    if (lowerTitle.includes('security') || lowerTitle.includes('cyber')) return '🔒';
    if (lowerTitle.includes('transport') || lowerTitle.includes('vehicle')) return '⚡';
    if (lowerTitle.includes('entertainment') || lowerTitle.includes('streaming')) return '🎬';
    if (lowerTitle.includes('finance') || lowerTitle.includes('crypto')) return '💰';
    return '📰'; // Default news emoji
  };

  // Format air/broadcast date for display
  const formatAirDate = (dateString?: string): string => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);
      const diffInDays = Math.floor(diffInHours / 24);

      // Format the actual date
      const actualDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });

      // If within 24 hours, show relative time + date
      if (diffInHours < 24) {
        if (diffInHours < 1) {
          return `Just now, ${actualDate}`;
        } else if (diffInHours < 2) {
          return `1 hour ago, ${actualDate}`;
        } else {
          return `${Math.floor(diffInHours)} hours ago, ${actualDate}`;
        }
      }

      // If within 30 days, show "X days ago" + date
      if (diffInDays <= 30) {
        if (diffInDays === 1) {
          return `1 day ago, ${actualDate}`;
        } else {
          return `${diffInDays} days ago, ${actualDate}`;
        }
      }

      // For older content, show weeks/months + date
      const diffInWeeks = Math.floor(diffInDays / 7);
      const diffInMonths = Math.floor(diffInDays / 30);

      if (diffInDays <= 60) { // Up to ~2 months
        if (diffInWeeks === 1) {
          return `1 week ago, ${actualDate}`;
        } else {
          return `${diffInWeeks} weeks ago, ${actualDate}`;
        }
      } else {
        if (diffInMonths === 1) {
          return `1 month ago, ${actualDate}`;
        } else {
          return `${diffInMonths} months ago, ${actualDate}`;
        }
      }
    } catch (error) {
      console.warn('Error formatting date:', error);
      return '';
    }
  };

  if (isLoading) {
    return (
      <div className={styles['newng-more-news-section']}>
        <h2 className={styles['newng-section-title']}>MORE NEWS</h2>
        <div className={styles['newng-more-news']}>
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className={`${styles['newng-article-card']} ${styles['newng-article-card--loading']}`}>
              <div className={`${styles['newng-article-image']} ${styles['newng-loading-placeholder']}`}></div>
              <div className={styles['newng-article-content']}>
                <div className={`${styles['newng-article-title']} ${styles['newng-loading-placeholder']}`}>Loading latest news...</div>
                <div className={`${styles['newng-article-summary']} ${styles['newng-loading-placeholder']}`}>Fetching content from our news sources...</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['newng-more-news-section']}>
        <h2 className={styles['newng-section-title']}>MORE NEWS</h2>
        <div className={styles['newng-error-message']}>
          <div className={styles['newng-error-icon']}>⚠️</div>
          <div className={styles['newng-error-text']}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['newng-more-news-section']}>
      <h2 className={styles['newng-section-title']}>MORE NEWS</h2>
      <div className={styles['newng-more-news']}>
        {articles.map((article) => (
          <div
            key={article.id}
            className={styles['newng-article-card']}
            data-is-live={article.isLive}
            onClick={() => handleArticleClick(article)}
            style={{ cursor: 'pointer' }}
          >
            <div className={styles['newng-article-image']}>
              {article.image.startsWith('/') ? (
                <img
                  src={article.image}
                  alt={article.title}
                  onError={() => handleImageError(article.id)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span>{article.image}</span>
              )}
              {article.isLive && (
                <div className={styles['newng-article-live-badge']}>LIVE</div>
              )}
            </div>
            <div className={styles['newng-article-content']}>
              <div className={styles['newng-article-title']}>{article.title}</div>
              <div className={styles['newng-article-summary']}>{article.summary}</div>
              {article.airDate && (
                <div className={styles['newng-article-airdate']}>
                  Aired: {formatAirDate(article.airDate)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};